// Indexparameters — de maandprijs waarmee een NIET-dynamisch contract afrekent.
//
// Achtergrond. De verkoopprijs is altijd `coeff × marktprijs + majoratie`, maar
// welke marktprijs dat is, hangt af van de prijsformule op de factuur:
//
//   Belpex_RLP_Hourly / Belpex_SPP_Hourly / Belpex / Hourly Belpex
//        → DYNAMISCH: de echte spotprijs van dat kwartier (EPEX, uit Lynx).
//   Belpex_RLP / Belpex_SPP / TTF RLP
//        → NIET-dynamisch: één indexwaarde PER MAAND, die niet uit een marktfeed
//          komt maar afgesproken/gepubliceerd is. Die zetten we in de admin.
//
// Daarom matchen we het type EXACT (na normalisatie) en niet met `includes`:
// "Belpex_RLP" en "Belpex_RLP_Hourly" verschillen maar in één woord en betekenen
// het tegenovergestelde. Een substring-match zou elke dynamische klant stil op de
// maandindex zetten. Gemeten op de commissie-cache (57.885 rijen) komen exact deze
// waarden voor: Belpex_RLP, Belpex_RLP_Hourly, Belpex, Hourly Belpex (afname EL),
// Belpex_SPP, Belpex_SPP_Hourly, Belpex, Hourly Belpex (injectie EL) en TTF RLP
// (afname gas) — let op de SPATIE in "TTF RLP" tegenover het liggende streepje in
// de EL-types; vandaar dat de normalisatie alle scheidingstekens weghaalt.
//
// EENHEID. De admin voert de index in zoals hij gepubliceerd wordt: c€/kWh
// (4,81 c€/kWh). De verkoopprijsformule rekent in EUR/MWh, want dáárin staan de
// coëfficiënt en de majoratie op de factuur. 1 c€/kWh = 10 EUR/MWh; die factor
// staat hier één keer (cEurKwhNaarEurMwh) en nergens anders in de code.

// De drie indexreeksen. `market` + `richting` bepalen waar de reeks geldt; `types`
// zijn de genormaliseerde prijsformule-types die erop uitkomen.
export const INDEX_CODES = [
  {
    code: 'BELPEX_RLP', label: 'Belpex_RLP', market: 'EL', richting: 'afname',
    types: ['belpexrlp'], omschrijving: 'Afname elektriciteit (EL Type Offtake = Belpex_RLP)',
  },
  {
    code: 'BELPEX_SPP', label: 'Belpex_SPP', market: 'EL', richting: 'injectie',
    types: ['belpexspp'], omschrijving: 'Injectie elektriciteit (EL Type Injection = Belpex_SPP)',
  },
  {
    code: 'TTF_RLP', label: 'TTF_RLP', market: 'NG', richting: 'afname',
    types: ['ttfrlp'], omschrijving: 'Afname gas (NG Type Offtake = TTF RLP)',
  },
];

export const INDEX_CODE_LIJST = INDEX_CODES.map((c) => c.code);

// Scheidingstekens en hoofdletters weg: "TTF RLP", "ttf_rlp" en "TTF-RLP" zijn
// dezelfde formule, "Belpex_RLP_Hourly" blijft een andere.
export const normaliseerType = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Het prijsformule-type → de indexreeks, of null wanneer het contract dynamisch is
// (of het type onbekend/leeg is). `market` en `richting` moeten meekloppen: een
// injectieformule mag nooit de afname-index voeden.
export function indexCodeVoor(type, market, richting) {
  const n = normaliseerType(type);
  if (!n) return null;
  for (const c of INDEX_CODES) {
    if (c.market !== market) continue;
    if (richting && c.richting !== richting) continue;
    if (c.types.includes(n)) return c.code;
  }
  return null;
}

export const indexMeta = (code) => INDEX_CODES.find((c) => c.code === code) || null;

// c€/kWh → EUR/MWh. Afronden op 6 decimalen houdt 4,81 × 10 op 48,1 in plaats van
// 48,099999999999994.
export const cEurKwhNaarEurMwh = (v) => {
  const n = Number(v);
  if (v == null || v === '' || !Number.isFinite(n)) return null;
  return Math.round(n * 10 * 1e6) / 1e6;
};
export const eurMwhNaarCEurKwh = (v) => {
  const n = Number(v);
  if (v == null || v === '' || !Number.isFinite(n)) return null;
  return Math.round((n / 10) * 1e6) / 1e6;
};

// ── Welke resolutie mag een prijs dragen? ──────────────────────────────────────
// Een meter die maar één keer per jaar gelezen wordt, levert geen dagprofiel; er
// een dag- of kwartierprijs naast leggen zou een detail suggereren dat er niet is.
// De klasse van de meter bepaalt dus de FIJNSTE resolutie waarop we een prijs
// tonen — grover mag altijd:
//   SMR3 / AMR        → kwartier (dus dag-, maand- én jaarweergave)
//   SMR1 / SMR2 / MMR → maand (dus maand- en jaarweergave)
//   YMR en al de rest → jaar (enkel de jaarweergave)
// De onbekende metermethode valt bewust op de GROOFSTE klasse terug: de
// jaarweergave toont maandgemiddelden, en die zijn voor elke meter waar.
export function meterKlasse(meterMethod) {
  const m = String(meterMethod || '').toUpperCase();
  if (m.includes('SMR3') || m === 'SMR 3') return 'kwartier';
  if (m.includes('AMR')) return 'kwartier';
  if (m.includes('SMR1') || m === 'SMR 1' || m.includes('SMR2') || m === 'SMR 2') return 'maand';
  if (m.includes('MMR')) return 'maand';
  return 'jaar';
}

// De weergaven (viewMode van de Metering-pagina) waarop deze meter een prijs mag
// dragen. 'day' = kwartierbuckets, 'month' = dagbuckets, 'year' = maandbuckets.
const KLASSE_WEERGAVEN = {
  kwartier: ['day', 'month', 'year'],
  maand: ['month', 'year'],
  jaar: ['year'],
};
export const prijsWeergavenVoorMeter = (meterMethod) => KLASSE_WEERGAVEN[meterKlasse(meterMethod)];

// Mag de prijsreeks aan bij deze selectie? Alle aangevinkte aansluitingen moeten
// hem kunnen dragen — anders zou één jaarmeter tussen drie kwartiermeters een
// prijslijn krijgen over buckets waarvoor hij geen meting heeft.
export function prijsMogelijk(meterMethods, viewMode) {
  const lijst = meterMethods || [];
  if (!lijst.length) return false;
  return lijst.every((m) => prijsWeergavenVoorMeter(m).includes(viewMode));
}

// Waarom staat de knop er niet? Eén zin, zodat het scherm het verschil kan tonen
// tussen "deze meter kan dit niet" en "er is niets geselecteerd".
export function prijsUitleg(meterMethods, viewMode) {
  const lijst = meterMethods || [];
  if (!lijst.length) return 'Vink eerst een aansluiting aan.';
  if (prijsMogelijk(lijst, viewMode)) return '';
  const klassen = [...new Set(lijst.map(meterKlasse))];
  const grofste = klassen.includes('jaar') ? 'jaar' : klassen.includes('maand') ? 'maand' : 'kwartier';
  if (grofste === 'jaar') return 'Deze meter wordt jaarlijks gelezen — prijzen enkel op de jaarweergave.';
  return 'Deze meter wordt maandelijks gelezen — prijzen enkel op de maand- of jaarweergave.';
}

// ── Indexwaarden ──────────────────────────────────────────────────────────────
// De admin-API levert per code een object met maandsleutels: { '2025-01': 4.81 }.
// Waarden staan in c€/kWh; deze functie geeft EUR/MWh terug, want dat is de
// eenheid waarin de rest van de prijsketen (spot, coëfficiënt, majoratie) rekent.
// Ontbreekt de maand, dan geven we null — géén 0 en géén buurmaand: een prijs van
// nul is een bewering, en de buurmaand doen alsof is dat evengoed.
export function indexEurMwh(parameters, code, maandSleutel) {
  if (!parameters || !code || !maandSleutel) return null;
  const reeks = parameters[code];
  if (!reeks) return null;
  const raw = reeks[maandSleutel];
  if (raw == null || raw === '') return null;
  return cEurKwhNaarEurMwh(raw);
}

// De maand ('YYYY-MM') waar een bucketsleutel van de grafiek in valt.
//   quarter → de sleutel is 'HH:MM' (tijd van de dag); de maand komt van de dag
//             die op het scherm staat.
//   day     → 'YYYY-MM-DD'
//   month   → 'YYYY-MM'
export function maandVanBucket(key, granularity, dag) {
  if (granularity === 'quarter') return String(dag || '').substring(0, 7) || null;
  const k = String(key || '');
  return k.length >= 7 ? k.substring(0, 7) : null;
}

// ── Prijsbron per aansluiting en stroomrichting ────────────────────────────────
// `tarief` is het contract van de aansluiting (uit de meest recente factuur):
// { market, coeffOfftake, majOfftake, typeOfftake, coeffInjection, … }.
// Levert { soort: 'index', code } of { soort: 'spot' } — of null wanneer er voor
// die richting geen contract is (dan hoort er ook geen prijs te staan).
//
// Gas kent geen spotreeks in deze module: zonder TTF-index is er dus geen prijs.
// Dat is bewust — een gasprijs uit de elektriciteitsspot afleiden zou onzin zijn.
export function prijsBron(tarief, richting = 'afname') {
  if (!tarief) return null;
  const market = tarief.market === 'NG' ? 'NG' : 'EL';
  const type = richting === 'injectie' ? tarief.typeInjection : tarief.typeOfftake;
  const heeftVoorwaarde = richting === 'injectie'
    ? Boolean(tarief.coeffInjection || tarief.majInjection)
    : Boolean(tarief.coeffOfftake || tarief.majOfftake);
  if (!heeftVoorwaarde) return null;
  const code = indexCodeVoor(type, market, richting);
  if (code) return { soort: 'index', code };
  if (market === 'NG') return null;
  return { soort: 'spot' };
}

// De basisprijs (EUR/MWh) voor één bucket en één richting: de maandindex bij een
// niet-dynamisch contract, anders de spotprijs die de beller al per bucket heeft
// klaarstaan.
export function basisprijs(tarief, richting, { spot = null, parameters = null, maand = null } = {}) {
  const bron = prijsBron(tarief, richting);
  if (!bron) return null;
  if (bron.soort === 'index') return indexEurMwh(parameters, bron.code, maand);
  return spot == null || !Number.isFinite(Number(spot)) ? null : Number(spot);
}

// De indexreeksen die bij deze selectie horen — voor de legende en de voetnoot
// onder de grafiek. Eén code per (market, richting) die in de tarieven voorkomt.
export function actieveIndexCodes(tarieven) {
  const uit = new Set();
  for (const t of tarieven || []) {
    for (const r of ['afname', 'injectie']) {
      const bron = prijsBron(t, r);
      if (bron && bron.soort === 'index') uit.add(bron.code);
    }
  }
  return INDEX_CODE_LIJST.filter((c) => uit.has(c));
}
